import { ensureMatterArtifactPrivacy } from './ensure-matter-artifact-privacy'
import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'

import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { kMatterArtifactVersionsBasePath } from '@/matter-artifacts/constants'
import { MatterArtifactVersionReadSchema, type MatterArtifactVersionRead } from '@/matter-artifacts/schemas'

export async function getMatterArtifactVersion(matterArtifactVersionId: string): Promise<MatterArtifactVersionRead> {
  const transactionId = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error("Could not retrieve user's authentication token.")
  await ensureMatterArtifactPrivacy(matterArtifactVersionId, token)

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kMatterArtifactVersionsBasePath}/${encodeURIComponent(matterArtifactVersionId)}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Transaction-Id': transactionId,
    },
  })
  const { data, parseError } = await readResponseJson(response)

  if (!response.ok) {
    throw new ApiError(response.status, getErrorDetail(data) || `Failed to fetch Matter artifact version: ${response.status}`)
  }

  if (parseError) {
    Sentry.captureException(new Error('Matter artifact version response JSON parse failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        matterArtifactVersionId,
        parseError: formatUnknownError(parseError),
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid Matter artifact version response JSON')
  }

  const parsed = MatterArtifactVersionReadSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(new Error('Matter artifact version response schema validation failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        matterArtifactVersionId,
        responseKeys: getTopLevelKeys(data),
        issues: parsed.error.issues,
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid Matter artifact version response shape', parsed.error.issues)
  }

  return parsed.data
}

function getErrorDetail(data: unknown): string | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail
  if (detail === undefined || detail === null) return null
  return JSON.stringify(detail)
}
